import { getUserRole } from '../base/user-role/functions';
import { getIsLobbyVisible } from '../lobby/functions';
import { isPrejoinPageVisible } from '../prejoin/functions';
import { hasPersistentAuthToken } from '../vrs-auth/storage';
/**
 * Whether the toolbar shows the signed-in client's call actions (Invite,
 * Request Interpreter, the client ordering): a signed-in client, in a room,
 * past pre-join and the lobby, and not a guest or observer on a guest link.
 * Shared by the Toolbox and the buttons it gates so they cannot disagree.
 *
 * @param {IReduxState} state - The redux state.
 * @returns {boolean}
 */
export function hasClientCallActions(state) {
    const roomRole = state['features/base/jwt'].user?.role;
    return hasPersistentAuthToken() && getUserRole() === 'client'
        && Boolean(state['features/base/conference'].room)
        && roomRole !== 'guest' && roomRole !== 'observer'
        && !isPrejoinPageVisible(state) && !getIsLobbyVisible(state);
}
/**
 * Below this width the bar's labelled call actions (Request Interpreter, the
 * caller's green Interpreter) are 48 px icon keys like the rest of the bar,
 * so hang-up always fits on a phone.
 */
export const COMPACT_CALL_BAR_WIDTH = 520;
/**
 * Whether the call bar is narrow enough for icon-key call actions.
 *
 * @param {IReduxState} state - The redux state.
 * @returns {boolean}
 */
export function isCompactCallBar(state) {
    return state['features/base/responsive-ui'].clientWidth < COMPACT_CALL_BAR_WIDTH;
}
/**
 * How many 52 px bar places a labelled call action takes: its own width
 * (wideSlots), or one key on a narrow bar.
 *
 * @param {boolean} compact - Whether the bar is narrow (isCompactCallBar).
 * @param {number} wideSlots - The places its label takes on a wide bar.
 * @returns {number}
 */
export function callActionSlots(compact, wideSlots) {
    return compact ? 1 : wideSlots;
}
/** One bar place: a 48 px key and its 4 px gap. */
export const CALL_BAR_PLACE = 52;
/** The bar's own padding at its two ends. */
export const CALL_BAR_PADDING = 12;
/**
 * How many 52 px places a bar this wide has.
 *
 * @param {number} width - The bar width (responsive-ui clientWidth).
 * @returns {number}
 */
export function callBarPlaces(width) {
    return Math.floor((width - CALL_BAR_PADDING) / CALL_BAR_PLACE);
}
/**
 * Splits a signed-in client's call actions between the bar and More.
 *
 * Robert, 2026-10-29: "if the task bar has space, no need for the 'more'
 * button - can just show all options". When every action fits beside the
 * fixed keys (hang-up, Move, the labelled call actions), all of them show in
 * their priority order and More does not show. Only a bar that is too narrow
 * gets More: then it holds the actions after the first ones that fit, and the
 * More-only actions (Schedule for later) go last in it.
 *
 * @param {Array} actions - The enabled actions in priority order (Mic and Camera first).
 * @param {Object} options - The bar.
 * @param {number} options.width - The bar width.
 * @param {number} options.fixedPlaces - Places of the keys that always show: hang-up, Move and the labelled actions.
 * @param {number} options.minimum - The actions that stay on the bar however narrow it is (Mic, Camera, CC).
 * @param {number} options.limit - The most actions that the bar shows beside More.
 * @param {Function} options.moreOnly - Whether an action goes to More when More shows.
 * @returns {ICallBarSplit}
 */
export function splitClientCallBar(actions, options) {
    const places = callBarPlaces(options.width) - options.fixedPlaces;
    if (actions.length <= places) {
        return { bar: actions.slice(), more: [] };
    }
    const barCandidates = actions.filter(action => !options.moreOnly(action));
    const count = Math.max(options.minimum, Math.min(options.limit, places - 1));
    return {
        bar: barCandidates.slice(0, count),
        more: [...barCandidates.slice(count), ...actions.filter(options.moreOnly)]
    };
}
